type TransitionCallback = () => void;
type TransitionOptions = {
  scrollToTop?: boolean;
};

let isTransitionRunning = false;
let isSmoothScrollPending = false;

const SCROLL_OPTS: ScrollToOptions = { top: 0, left: 0, behavior: "auto" };

/**
 * Scroll every common scroll root used by follower / dashboard layouts.
 * Duplicate `id="app-wrapper"` nodes exist in some trees — use querySelectorAll.
 */
export function scrollAppContainersToTop() {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  try {
    window.scrollTo(SCROLL_OPTS);
  } catch {}
  try {
    document.documentElement.scrollTo(SCROLL_OPTS);
  } catch {}
  try {
    document.body.scrollTo(SCROLL_OPTS);
  } catch {}

  const selectors = [
    '[id="app-wrapper"]',
    ".content-container",
    ".stay-up-to-date-wrapper",
    "#landing-wrapper",
    ".uptodate-wrapper",
    ".feed-optimized-transition-wrap",
    ".landing-wrapper",
    "main",
  ];

  for (const sel of selectors) {
    try {
      document.querySelectorAll(sel).forEach((el) => {
        (el as HTMLElement).scrollTo(SCROLL_OPTS);
      });
    } catch {
      // ignore
    }
  }
}

/** Scroll after layout; retries catch React commit + images/layout settling after SPA navigations. */
export function scheduleScrollToTop() {
  if (typeof window === "undefined") return;
  const runPasses = () => {
    scrollAppContainersToTop();
    setTimeout(scrollAppContainersToTop, 0);
    setTimeout(scrollAppContainersToTop, 50);
    setTimeout(scrollAppContainersToTop, 120);
  };
  requestAnimationFrame(() => {
    requestAnimationFrame(runPasses);
  });
}

export const viewTransitionNavigate = (
  callback: TransitionCallback,
  options: TransitionOptions = {}
) => {
  if (typeof document === "undefined") {
    callback();
    if (options.scrollToTop) {
      scheduleScrollToTop();
    }
    return;
  }

  const startViewTransition = (document as any)?.startViewTransition;
  if (typeof startViewTransition !== "function" || isTransitionRunning) {
    callback();
    if (options.scrollToTop) {
      scheduleScrollToTop();
    }
    return;
  }

  try {
    isTransitionRunning = true;
    const transition = startViewTransition(() => {
      callback();
      if (options.scrollToTop) {
        scheduleScrollToTop();
      }
    });

    if (transition?.finished) {
      transition.finished.finally(() => {
        isTransitionRunning = false;
      });
      if (options.scrollToTop) {
        transition.finished.then(() => {
          scheduleScrollToTop();
        });
      }
    } else {
      isTransitionRunning = false;
    }
  } catch (error) {
    isTransitionRunning = false;
    callback();
  }
};

const sanitizeViewTransitionName = (value: string) =>
  value.replace(/[^a-zA-Z0-9_-]/g, "_");

export const getComponentViewTransitionName = (component?: {
  unique_id?: string;
  route_name?: string;
  id?: string;
}) => {
  const rawId =
    component?.route_name || component?.unique_id || component?.id || "";
  if (!rawId) return undefined;
  return `component-${sanitizeViewTransitionName(String(rawId))}`;
};

const resolveScrollTarget = (
  scrollTarget?: Window | HTMLElement | null
): Window | HTMLElement | null => {
  if (scrollTarget) return scrollTarget;
  if (typeof document === "undefined") return null;
  return (document.scrollingElement as HTMLElement) || document.documentElement;
};

const scheduleSmoothScrollToTop = (
  scrollTarget?: Window | HTMLElement | null
) => {
  if (isSmoothScrollPending) return;
  isSmoothScrollPending = true;

  const target = resolveScrollTarget(scrollTarget);
  if (!target) {
    isSmoothScrollPending = false;
    return;
  }

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (target === window) {
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      } else {
        target.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      }
      setTimeout(() => {
        isSmoothScrollPending = false;
      }, 300);
    });
  });
};

export const navigateWithViewTransitionAndSmoothScroll = (
  navigateFn: TransitionCallback,
  scrollTarget?: Window | HTMLElement | null
) => {
  if (typeof document === "undefined") {
    navigateFn();
    scheduleSmoothScrollToTop(scrollTarget);
    return;
  }

  const startViewTransition = (document as any)?.startViewTransition;
  if (typeof startViewTransition !== "function" || isTransitionRunning) {
    navigateFn();
    scheduleSmoothScrollToTop(scrollTarget);
    return;
  }

  try {
    isTransitionRunning = true;
    const transition = startViewTransition(() => {
      navigateFn();
      scheduleSmoothScrollToTop(scrollTarget);
    });

    if (transition?.finished) {
      transition.finished.finally(() => {
        isTransitionRunning = false;
      });
    } else {
      isTransitionRunning = false;
    }
  } catch (error) {
    isTransitionRunning = false;
    navigateFn();
    scheduleSmoothScrollToTop(scrollTarget);
  }
};

